// Angular and RJX Imports
// =========================================================
import { Injectable } from '@angular/core'
// AWS Imports
// =========================================================
import { generateClient } from 'aws-amplify/api'
import { type GraphQLSubscription } from 'aws-amplify/api'

// Axios Imports
// =========================================================
import axios from 'axios'
// Prime NG Imports
// =========================================================
import { DialogService } from 'primeng/dynamicdialog'
// Custom Imports
// =========================================================
import { ToastAlertComponent } from 'src/app/03_shared-components/01_alerts/toast-alert/toast-alert.component'
import { ExecuteDeleteAPI, ExecuteGetAPI, ExecutePostAPI } from './execute_api-calls'
import { TriggerApiCallsService } from '../services/cancel-api-call'
import {
  type ReviewOrdersOutputInterface,
  type ApproveOrdersInterface,
  type MarkAsPendingReviewInterface,
  type ReviewOrdersParametersInterface,
  type ExportBreaksInterface,
  type CalculateBreaksInterface,
  type DeleteBuyGroupInterface
} from '../interfaces/review-orders-interface'
import { type ResponseSubscription } from '../../../API'
import * as subscriptions from '../../../graphql/subscriptions'
import { type GridApi } from 'ag-grid-community'

@Injectable()
export class ReviewOrdersAPIs {
  private readonly client = generateClient()
  clearApiCalls: boolean = false

  constructor (
    public dialogService: DialogService,
    public toastAlert: ToastAlertComponent,
    public triggerApiCallsService: TriggerApiCallsService
  ) {
    // ApiCall Service
    this.triggerApiCallsService.triggerApiCalls$.subscribe({
      next: data => {
        this.clearApiCalls = data['clear_api_calls']
      }
    })
  }

  // Get All Grid Data for Review Orders
  async GetAllReviewOrdersUserInputData (productFilter?: string[]) {
    // the api call path with the parameter string is needed
    const path = '/review_orders/input'
    const messages = {
      success: '---> API Call Successful: Retrieved Review Orders Input Grid Data',
      error: 'Error retrieving review orders input grid data.'
    }

    return await ExecutePostAPI(path, this, { product_filter: productFilter || [] }, messages)
  }

  // Get All Grid Data for Review Orders
  async GetAllReviewOrdersOutputData (data: ReviewOrdersOutputInterface) {
    console.log('getallreviewordersoutputdata', data)
    // the api call path with the parameter string is needed
    const path = '/review_orders/output'
    const messages = {
      success: '---> API Call Successful: Retrieved Review Orders Output Grid Data',
      error: 'Error retrieving review orders output grid data.'
    }

    const reviewOrdersOutputData = await ExecutePostAPI(
      path,
      this,
      {
        job_ids: data.ids
      },
      messages
    )
    console.log('reviewOrdersOutputData: ', reviewOrdersOutputData)
    if (reviewOrdersOutputData?.is_success) {
      return await axios
        .get(reviewOrdersOutputData.data, {})
        .then(response => {
          console.log('response: ', response)
          return response
        })
        .catch(error => {
          console.error('Error retrieving review order output grid data: ', error)
          this.toastAlert.showError('Error Loading Data')
          throw error
        })
    } else {
      console.error('Error retrieving review order output grid file')
      this.toastAlert.showError('Error Loading Data')
      return {
        status: null,
        data: []
      }
    }
    // return {
    //   status: 200,
    //   data: {
    //     buy_summary: {
    //       row_data: [
    //         {
    //           job_id: 1,
    //           styc_id: 'Styc123',
    //           styc_name: 'suzette-brown',
    //           size_range: 'S-XXL',
    //           store_distro: 'All Stores',
    //           delivery_date: '07/24/2024',
    //           unit_qty: 1259,
    //           delivery_type: 'Set',
    //           chart: {},
    //           detailGridData: [{
    //             channel: 'stores',
    //             store_id: 'MS-001',
    //             index_pct: 0.01,
    //             target_qty: 13.7,
    //             buy_qty: 13,
    //             mismatch: 1.31,
    //             s_l: {
    //               buy: 3,
    //               target: 2
    //             },
    //             m_l: {
    //               buy: 4,
    //               target: 4
    //             },
    //             l_l: {
    //               buy: 11,
    //               target: 12.31
    //             },
    //             xl_l: {
    //               buy: 8,
    //               target: 8
    //             },
    //             xxl_l: {
    //               buy: 6,
    //               target: 5
    //             },
    //             target_vs_buy_chart: {},
    //             detail_row_sizes: [
    //               'S_L', 'M_L', 'L_L', 'XL_L', 'XXL_L'
    //             ]
    //           }],
    //           main_row_sizes: [
    //             'S_L', 'M_L', 'L_L', 'XL_L', 'XXL_L'
    //           ]
    //         }
    //       ]
    //     },
    //     additional_data: {
    //       delivery_ids: [1]
    //     }
    //   }
    // }
  }

  // Approve Orders
  async ApproveOrders (data: ApproveOrdersInterface) {
    // the api call path with the parameter string is needed
    const path = '/review_orders/approve'
    const numOfJobs = data.job_ids.length
    const suffix = numOfJobs > 1 ? 's' : ''
    const messages = {
      success: `Successfully approved ${numOfJobs} order${suffix}.`,
      error: `Error approving ${numOfJobs} order${suffix}.`,
      showSuccessToast: true
    }

    return await ExecutePostAPI(path, this, data, messages)
  }

  // Mark as Pending Review
  async OnMarkAsPendingReview (data: MarkAsPendingReviewInterface) {
    // the api call path with the parameter string is needed
    const path = '/review_orders/pending_review'
    const numOfJobs = data.job_ids.length
    const suffix = numOfJobs > 1 ? 's' : ''
    const messages = {
      success: `Successfully unapproved ${numOfJobs} order${suffix}.`,
      error: `Error unapproving ${numOfJobs} order${suffix}.`,
      showSuccessToast: true
    }

    return await ExecutePostAPI(path, this, data, messages)
  }

  // On Calculate breaks
  async OnCalculateBreaks (rows: CalculateBreaksInterface) {
    // the api call path with the parameter string is needed
    const path = '/review_orders/calculate_breaks'
    const messages = {
      success: 'Successfully ran calculate breaks.',
      error: 'Error running calculate breaks.',
      showSuccessToast: true
    }

    return await ExecutePostAPI(path, this, rows, messages)
  }

  JobGridDataSubscription (gridApi: GridApi, context, updateEnabledActions: Function): any {
    console.log('inside JobGridDataSubscription review orders')
    return this.client
      .graphql<GraphQLSubscription<ResponseSubscription>>({
      query: subscriptions.response
    })
      .subscribe({
        next: ({ data }) => {
          const { job_name, status } = data?.response || {}
          // if (status === 'success') {
          //   status = 'COMPLETED'
          // }

          if (!job_name || !gridApi || gridApi['destroyCalled']) {
            console.log('No job_name or gridApi not ready')
            return
          }

          console.log('🔍 Received update:', { job_name, status })
          context.configureUserInputGridData()

          const foundMatch = this.updateStatus(gridApi, job_name, status, updateEnabledActions)

          if (!foundMatch) {
            console.warn(`No matching row found for job_name: ${job_name}`)
          }
        },
        error: error => console.warn(error)
      })
  }

  updateStatus (gridApi: GridApi, job_name: string | number, status: string, updateEnabledActions: Function): boolean {
    // Try to find and update the matching row
    let foundMatch = false
    gridApi.forEachNode(masterNode => {
      if (!masterNode.data.detail_data) return
      console.log('Checking masterNode:', masterNode)
      masterNode.data.detail_data?.forEach(detail => {
        console.log('    Detail row:', {
          id: detail.id,
          job_name: detail.job_name,
          status: detail.status
        })
      })

      // Find matching detail row
      const detailRowIndex = masterNode.data.detail_data.findIndex(detail => {
        // Check if detail.id matches OR detail.job_name matches
        return (
          detail.id?.toString() === job_name?.toString() ||
                    detail.job_name?.toString() === job_name?.toString()
        )
      })

      if (detailRowIndex !== -1) {
        foundMatch = true
        const oldStatus = masterNode.data.detail_data[detailRowIndex].status

        console.log('Found matching row!', {
          job_name,
          oldStatus,
          newStatus: status,
          detailRow: masterNode.data.detail_data[detailRowIndex]
        })

        if (oldStatus !== status) {
          // master row's detail_data
          masterNode.data.detail_data[detailRowIndex].status = status
          masterNode.data.statuses[detailRowIndex] = status
          masterNode.setData({ ...masterNode.data, statuses: masterNode.data.statuses })

          // Refresh master row
          // gridApi.refreshCells({
          //   rowNodes: [masterNode],
          //   force: true,
          //   columns: ['statuses', 'delete']
          // })

          const detailGridInfo = gridApi.getDetailGridInfo(`detail_${masterNode.id}`)
          if (detailGridInfo?.api) {
            detailGridInfo.api.forEachNode(detailNode => {
              if (
                detailNode.data.id?.toString() === job_name?.toString() ||
                                detailNode.data.job_name?.toString() === job_name?.toString()
              ) {
                detailNode.setData({ ...detailNode.data, status })

                detailGridInfo.api.refreshCells({
                  rowNodes: [detailNode],
                  columns: ['parameters'],
                  force: true
                })
              }
            })
          }
        }
      }
    })
    updateEnabledActions()
    return foundMatch
  }

  // On Export Orders
  async OnExportBreaks (data: ExportBreaksInterface) {
    console.log('---> On Export Orders API Call: ', data)

    // the api call path with the parameter string is needed
    const path = '/review_orders/export'
    const messages = {
      success: 'Successfully exported orders.',
      error: 'Error exporting orders.',
      showSuccessToast: true
    }

    return await ExecutePostAPI(path, this, data, messages)
  }

  async OnDeleteBuyGroup (data: DeleteBuyGroupInterface) {
    console.log('---> On Delete Buy Group API Call: ', data)

    // the api call path with the parameter string is needed
    const path = `/review_orders/buy_groups/${(data.buy_group_id + '').replaceAll('/', '%2F')}`
    const messages = {
      success: 'Successfully deleted buy group.',
      error: 'Error deleting buy group.',
      showSuccessToast: true
    }

    return await ExecuteDeleteAPI(path, this, messages)
  }

  // Get Configuration Settings
  // For the User input -> settings modal
  async GetParameterSettings (rowID: string) {
    console.log('---> On Get Parameter Settings API Call: ', rowID)

    // the api call path with the parameter string is needed
    const path = `/review_orders/${rowID}`
    const messages = {
      success: `---> API Call Successful: Retrieved Parameter Settings For Order: ${rowID}.`,
      error: `Error retrieving parameter settings for order: ${rowID}.`
    }

    return await ExecuteGetAPI(path, this, messages)
  }

  // Submit Configuration Settings
  // For the User input -> settings modal
  async SubmitConfigurationSettings (data: ReviewOrdersParametersInterface) {
    console.log('---> On Submit Configuration Settings API Call: ', data)

    // the api call path with the parameter string is needed
    const path = '/review_orders/create'
    const messages = {
      success: `Successfully saved parameter settings for job: ${data.job_id}.`,
      error: `Error saving parameter settings for job: ${data.job_id}.`,
      showSuccessToast: true
    }

    return await ExecutePostAPI(path, this, data, messages)
  }
}
